import { Size } from '@ps/web-core-store';
import classNames from 'clsx';
import React, { useContext, useEffect } from 'react';
import { useSelector } from 'react-redux';
import type { RouteComponentProps } from 'react-router';
import { Route, Switch } from 'react-router';

import type { ModalContextValue } from '@swf/components/StarsModal/Context';
import { ModalContext } from '@swf/components/StarsModal/Context';
import { select } from '@swf/core/store';

import styles from './CasinoModal.scss';
import CasinoBuyReward from './components/CasinoBuyReward/CasinoBuyReward';
import CasinoError from './components/CasinoError/CasinoError';
import CasinoFSBError from './components/CasinoFSBError/CasinoFSBError';
import CasinoGameInfo from './components/CasinoGameInfo/CasinoGameInfo';
import CasinoGameOverlay from './components/CasinoGameOverlay/CasinoGameOverlay';
import CasinoGameSelector from './components/CasinoGameSelector/CasinoGameSelector';
import CasinoHelp from './components/CasinoHelp/CasinoHelp';
import CasinoPlayerSelector from './components/CasinoPlayerSelector/CasinoPlayerSelector';
import CasinoRaceLeaderboard from './components/CasinoRaceLeaderboard/CasinoRaceLeaderboard';
import CasinoRacePromoBan from './components/CasinoRacePromoBan/CasinoRacePromoBan';
import CasinoRewardInfo from './components/CasinoRewardInfo/CasinoRewardInfo';
import CasinoStakeSelector from './components/CasinoStakeSelector/CasinoStakeSelector';
import GameSessionError from './components/GameSessionError/GameSessionError';
import SetGamingLimit from './components/SetGamingLimit/SetGamingLimit';

interface LocationStateProps {
  pathname: string;
}
const gameProps = {
  size: Size.FLEX,
  closeOnOverlayClick: true,
  closeOnEsc: true,
  className: styles.flexFullFit
};

const CasinoModal: React.FC<RouteComponentProps> = (props) => {
  const { setStyle } = useContext<ModalContextValue>(ModalContext);
  const { pathname }: LocationStateProps = props.location || '';
  const desktopEmbedded = useSelector(select.device.isDesktopEmbedded)
    ? styles.desktopEmbedded
    : undefined;
  const mobileDevice = useSelector(select.device.isMobile) ? styles.mobileDevice : undefined;

  useEffect(() => {
    switch (pathname) {
      case '/casino/player-selector':
        setStyle({
          className: classNames(styles.playerselectorModal, desktopEmbedded),
          size: Size.SMALL
        });
        break;
      case '/casino/race-promo-ban':
      case '/casino/error':
        setStyle({ size: Size.MEDIUM });
        break;
      case '/casino/gaming-session-error':
        setStyle({ size: Size.MEDIUM });
        break;
      case '/casino/es-gaming-limit':
        setStyle({ size: Size.LARGE, className: classNames(desktopEmbedded) });
        break;
      case '/casino/stake-selector':
        setStyle({ size: Size.SMALL });
        break;
      case '/casino/game-selector':
        setStyle({
          ...gameProps,
          className: classNames(
            styles.flexFullFit,
            desktopEmbedded,
            mobileDevice,
            styles.gamePicker
          )
        });
        break;
      case '/casino/help':
        setStyle({
          ...gameProps,
          className: classNames(styles.flexFull, mobileDevice)
        });
        break;
      case '/casino/game-info':
        setStyle({
          ...gameProps,
          className: classNames(styles.flexFullFit, desktopEmbedded, styles.gameInfo)
        });
        break;
      case '/casino/race-leaderboard':
        setStyle({
          closeOnOverlayClick: true,
          closeOnEsc: true,
          size: Size.LARGE
        });
        break;
      default:
        setStyle({ size: Size.LARGE });
    }
  }, [pathname, setStyle, desktopEmbedded, mobileDevice]);

  return (
    <Switch>
      <Route path="/casino/player-selector" component={CasinoPlayerSelector} />
      <Route path="/casino/stake-selector" component={CasinoStakeSelector} />
      <Route path="/casino/help" component={CasinoHelp} />
      <Route path="/casino/buy-reward" component={CasinoBuyReward} />
      <Route path="/casino/game-overlay" component={CasinoGameOverlay} />
      <Route path="/casino/fsb-playing" component={CasinoFSBError} />
      <Route path="/casino/reward-info" component={CasinoRewardInfo} />
      <Route path="/casino/race-promo-ban" component={CasinoRacePromoBan} />
      <Route path="/casino/error" component={CasinoError} />
      <Route path="/casino/gaming-session-error" component={GameSessionError} />
      <Route path="/casino/es-gaming-limit" component={SetGamingLimit} />
      <Route path="/casino/game-selector" component={CasinoGameSelector} />
      <Route path="/casino/game-info" component={CasinoGameInfo} />
      <Route path="/casino/race-leaderboard" component={CasinoRaceLeaderboard} />
    </Switch>
  );
};

export default CasinoModal;
